import React from 'react';
import { Box, IconButton, Stack } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import CloseDialogButton from '@/components/buttons/CloseDialogButton';
import { PageHeader } from '@/components/layouts/PageHeader';
import { useFolioDrawerContext } from './FolioDrawerContext';

/**
 * Pinned header row for a {@link FolioDrawer}: close button, title, actions and the
 * maximise toggle. Rendered by FolioDrawer when it has a `title`, or directly by a
 * form that owns its layout. Close and maximise come from {@link FolioDrawerContext},
 * so it must be rendered inside a FolioDrawer.
 *
 * @param {object} props
 * @param {string} [props.title] Title text; omitted for headers with only actions (Preview).
 * @param {React.ReactNode} [props.icon] Icon shown beside the title.
 * @param {boolean} [props.isBeta=false] Shows a BETA badge after the title.
 * @param {React.ReactNode} [props.headerActions] Extra controls before the maximise toggle.
 */
export const FolioDrawerHeader = ({ title, icon, isBeta = false, headerActions }) => {
	const { onClose, canMaximise, isMaximised, setIsMaximised } = useFolioDrawerContext();

	return (
		<Stack
			direction='row'
			alignItems='center'
			spacing={1}
			sx={{ p: 2, pb: 1, flexShrink: 0 }}
		>
			<CloseDialogButton handleClose={onClose} />
			{title && <PageHeader title={title} icon={icon} isBeta={isBeta} compact />}
			<Box sx={{ flex: 1 }} />
			{headerActions}
			{canMaximise && (
				<IconButton
					onClick={() => setIsMaximised((prev) => !prev)}
					sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
				>
					{isMaximised ? (
						<CloseFullscreenIcon sx={{ fontSize: '14px' }} />
					) : (
						<OpenInFullIcon sx={{ fontSize: '14px' }} />
					)}
				</IconButton>
			)}
		</Stack>
	);
};

export default FolioDrawerHeader;
